import { Idiomorph } from "idiomorph";

export interface MorphIframeDocumentOptions {
  /** Region ids with unsaved local edits — these islands are not morphed. */
  protectedEditIds?: ReadonlySet<string>;
  /** Whether the user is actively focused inside an editable island. */
  userIsEditing?: boolean;
}

/**
 * IMPORTANT — realms: the live nodes come from the iframe's JS realm, so
 * `instanceof Element` against this module's globals is ALWAYS false for
 * them. Idiomorph has the same blind spot: its `morphStyle: "outerHTML"`
 * entry point runs `newContent instanceof Node` on the OLD node and throws
 * `newContent is not iterable` for every iframe document. Everything in this
 * file therefore duck-types via `nodeType` and only uses the realm-safe
 * `morphStyle: "innerHTML"` path (which never re-normalizes the old node).
 */

function asElement(node: Node | null | undefined): Element | null {
  if (!node || node.nodeType !== 1) return null;
  return node as Element;
}

function closestEditableIsland(node: Node | null): Element | null {
  const el =
    asElement(node) ?? asElement((node as ChildNode | null)?.parentElement);
  if (!el || typeof el.closest !== "function") return null;
  return el.closest("[data-edit-id]:not([data-no-edit])");
}

function getEditableIslandEditId(node: Node): string | null {
  return closestEditableIsland(node)?.getAttribute("data-edit-id") ?? null;
}

function isProtectedIslandNode(
  node: Node,
  protectedEditIds?: ReadonlySet<string>,
): boolean {
  if (!protectedEditIds || protectedEditIds.size === 0) return false;
  const editId = getEditableIslandEditId(node);
  return editId !== null && protectedEditIds.has(editId);
}

/** True when removing `node` would take a protected island with it. */
function containsProtectedIsland(
  node: Node,
  protectedEditIds?: ReadonlySet<string>,
): boolean {
  if (!protectedEditIds || protectedEditIds.size === 0) return false;
  const el = asElement(node);
  if (!el || typeof el.querySelectorAll !== "function") return false;
  const islands = el.querySelectorAll("[data-edit-id]:not([data-no-edit])");
  for (const island of Array.from(islands)) {
    const editId = island.getAttribute("data-edit-id");
    if (editId !== null && protectedEditIds.has(editId)) return true;
  }
  return false;
}

function isCohesiveInjectedNode(node: Node): boolean {
  const el = asElement(node);
  if (!el || typeof el.hasAttribute !== "function") return false;
  return (
    el.hasAttribute("data-cohesive-nav-guard") ||
    el.hasAttribute("data-cohesive-force-light") ||
    el.hasAttribute("data-cohesive-edit") ||
    el.hasAttribute("data-cohesive-custom") ||
    el.hasAttribute("data-cohesive-injected")
  );
}

/** Copy `source`'s attributes onto `target`, removing ones absent in `source`. */
function syncElementAttributes(target: Element, source: Element): void {
  for (const attr of Array.from(target.attributes)) {
    if (!source.hasAttribute(attr.name)) {
      target.removeAttribute(attr.name);
    }
  }
  for (const attr of Array.from(source.attributes)) {
    if (target.getAttribute(attr.name) !== attr.value) {
      target.setAttribute(attr.name, attr.value);
    }
  }
}

/**
 * Sync head children by outerHTML identity, mirroring Idiomorph's
 * `head: {style: "merge"}`: keep old nodes that match new content verbatim,
 * remove old nodes absent from the new head (except cohesive-injected ones),
 * and append the genuinely new nodes. New nodes are materialized with
 * `createContextualFragment` in the LIVE document so appended scripts execute,
 * matching Idiomorph's head behavior.
 */
function mergeHeadElements(
  liveDoc: Document,
  liveHead: Element,
  parsedHead: Element,
): void {
  const newByHtml = new Map<string, Element>();
  for (const child of Array.from(parsedHead.children)) {
    newByHtml.set(child.outerHTML, child);
  }

  for (const oldChild of Array.from(liveHead.children)) {
    if (newByHtml.has(oldChild.outerHTML)) {
      newByHtml.delete(oldChild.outerHTML);
      continue;
    }
    if (isCohesiveInjectedNode(oldChild)) continue;
    oldChild.remove();
  }

  for (const newChild of newByHtml.values()) {
    const fragment = liveDoc
      .createRange()
      .createContextualFragment(newChild.outerHTML);
    liveHead.appendChild(fragment);
  }
}

/**
 * Morph prepared iframe HTML into a live iframe document without a full
 * `srcDoc` navigation. Call after the first load when content identity changes.
 *
 * The `<html>` and `<body>` attributes are synced manually, the head is
 * merged, and the body children are morphed via Idiomorph. Islands with
 * unsaved edits (`protectedEditIds`) and the island the user is focused in
 * are left untouched so local drafts and the caret survive.
 */
export function morphPreparedHtmlIntoDocument(
  liveDoc: Document,
  preparedHtml: string,
  options: MorphIframeDocumentOptions = {},
): void {
  const { protectedEditIds, userIsEditing = false } = options;
  const parsed = new DOMParser().parseFromString(preparedHtml, "text/html");
  const liveRoot = liveDoc.documentElement;
  const parsedRoot = parsed.documentElement;

  if (!liveRoot || !parsedRoot) {
    throw new Error("morphPreparedHtmlIntoDocument: missing documentElement");
  }
  const liveBody = liveDoc.body;
  const parsedBody = parsed.body;
  if (!liveBody || !parsedBody) {
    throw new Error("morphPreparedHtmlIntoDocument: missing body");
  }

  const activeIsland = userIsEditing
    ? closestEditableIsland(liveDoc.activeElement)
    : null;

  syncElementAttributes(liveRoot, parsedRoot);
  if (liveDoc.head && parsed.head) {
    mergeHeadElements(liveDoc, liveDoc.head, parsed.head);
  }
  syncElementAttributes(liveBody, parsedBody);

  Idiomorph.morph(liveBody, Array.from(parsedBody.childNodes), {
    morphStyle: "innerHTML",
    callbacks: {
      beforeNodeMorphed(oldNode: Node) {
        // Skipping a node skips its whole subtree, so refusing the island
        // node itself is enough to keep a draft or the caret intact while
        // the rest of the document updates around it.
        if (activeIsland && oldNode === activeIsland) {
          return false;
        }
        if (isProtectedIslandNode(oldNode, protectedEditIds)) {
          return false;
        }
        return true;
      },
      beforeNodeRemoved(node: Node) {
        if (isCohesiveInjectedNode(node)) {
          return false;
        }
        if (
          activeIsland &&
          (node === activeIsland || node.contains(activeIsland))
        ) {
          return false;
        }
        if (isProtectedIslandNode(node, protectedEditIds)) {
          return false;
        }
        if (containsProtectedIsland(node, protectedEditIds)) {
          return false;
        }
        return true;
      },
    },
  });
}
